import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './ProductActivityPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { useHelpPref } from '../lib/preferences'
import { fileTask, useTasks } from '../components/TaskCentre'
import { workbook, XLSX_TYPE } from './batches/xlsx'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PairTabs, productTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { todayLocal } from './timesheets/time'
import { DatePeriod, datesBetween, rangeLabel } from './productActivity/DatePeriod'
import { sharedPeriod, useSharedPeriod } from './productActivity/sharedPeriod'
import { datesLabel } from '../lib/keptDates'
import { StillLooking } from '../components/StillLooking'
import { CycleDrawer, type Tab as DrawerTab } from './productActivity/CycleDrawer'
import { BulkModal, BULK_BUTTONS, type BulkOp } from './productActivity/BulkModal'
import { LateDataPanel, type LateCounts } from './productActivity/LateDataPanel'
import { can, hasPower, useAccess } from '../lib/access'
import { WeightImportDialog } from './productActivity/WeightImportDialog'
import { ItemName, itemName } from './productActivity/itemName'
import { useLayer } from '../components/layers'
import { PendingButton, SkeletonRows } from '../components/pending'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { useLive } from '../lib/live'
import {
  CHIP,
  CHIP_ORDER,
  EST_TITLE,
  WEIGHT_EST_TITLE,
  FLAG_FILTERS,
  MAP_STATUS,
  NOT_CLOCKED,
  NO_MORE,
  NO_QUALITY,
  STATE_TEXT,
  TIME_BASES,
  TONE,
  dayLabel,
  dayShort,
  estKg,
  kg,
  mapSplit,
  matchesMore,
  moreCount,
  moreSummary,
  needsAttention,
  pct,
  strictest,
  exportRows,
  toneOf,
  unweighedSummary,
  type Cycle,
  type DayTone,
  type Flag,
  type ListResponse,
  type MoreFilters,
  type TimeBasis,
} from './productActivity/model'
import { openableRow } from '../lib/openableRow'

// Leaflet loads only when someone opens the Map view.
const CycleMap = lazy(() => import('./productActivity/CycleMap'))

// Product Activity (CONTROL V4 "Product activity", handoff §7g): the product
// cycles of a date range, one row per cycle. Every weight on a row is the API's
// effective value; this screen filters, formats and explains. A correction is
// an audited adjustment on top of the captured cycle, never an overwrite.

const HELP: [string, string][] = [
  [
    'One row is one product cycle',
    'A cycle is one use of one physical item: a bucket claimed by a picker, then weighed or receipted. The engine builds it from the raw scans, so a row here is an interpretation of events, not a record someone typed.',
  ],
  [
    'Why a cycle can look wrong',
    'Scans arrive late and out of order. Two pickers can claim the same bucket, a bucket can be weighed twice, a receipt can turn up with no claim against it. The processing rules decide what happens in each case, and every row can tell you which rule decided it.',
  ],
  [
    'Live, closed and locked days',
    'A live day is still receiving scans and can change under you. A closed day holds anything arriving late for approval. A locked day has been paid and has to be unlocked before a cycle can be changed. The banner says which day you are looking at.',
  ],
  [
    'Fixing one, and what it leaves behind',
    'Open a cycle to reassign the worker, correct the weight, exclude it or attach a quality scan the engine could not tie to it. The raw events are never edited. A correction sits on top of them with who made it and why, and the cycle says it was resolved by hand rather than by a rule.',
  ],
  [
    // h 2482-2483.
    'The map and the export follow the filters',
    'Both work on what is currently in view, not the whole season, so narrow the period, worker or picking job first. The export runs as a background task: carry on working and collect the file from Background tasks.',
  ],
]

const ALL_WORKERS = 'All workers'
const ALL_JOBS = 'All jobs'
const ALL_PRODUCTS = 'All products'
const UNASSIGNED = 'Unassigned'

export function ProductActivityPage() {
  const { user } = useAuth()
  const today = todayLocal()
  // A link from Batches or a report opens a day, a search or one cycle (?from,
  // ?to, ?q, ?cycle), or a filter (?worker, ?job, ?product, ?flag; §7l.4).
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { startTask } = useTasks()
  const linked = (k: string) => {
    const v = params.get(k)
    return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null
  }
  // Otherwise the period this visit last had open here, in Batches or in
  // Reports (§7l.3), else today.
  const [from, setFrom] = useState(() => linked('from') || sharedPeriod(today, user?.uid).from)
  const [to, setTo] = useState(() => linked('to') || linked('from') || sharedPeriod(today, user?.uid).to)
  const kept = useSharedPeriod(user?.uid, today, from, to)
  const [data, setData] = useState<ListResponse | null>(null)
  // The period the rows on screen belong to: a first load or a new period
  // draws skeleton rows, a reload of the same period after a correction does
  // not (h 3994-4002).
  const [shown, setShown] = useState<string | null>(null)
  const loading = shown !== `${from}_${to}`
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState('')

  const [tab, setTab] = useState<'included' | 'excluded' | 'late'>('included')
  // Late data (D7): the badge count, and a number that tells the panel to load again.
  const access = useAccess()
  const [lateCount, setLateCount] = useState<number | null>(null)
  const [lateKey, setLateKey] = useState(0)
  const [query, setQuery] = useState(() => params.get('q') || '')
  const [worker, setWorker] = useState(() => params.get('worker') || ALL_WORKERS)
  const [job, setJob] = useState(() => params.get('job') || ALL_JOBS)
  const [product, setProduct] = useState(() => params.get('product') || ALL_PRODUCTS)
  const [drop, setDrop] = useState<'worker' | 'job' | 'product' | null>(null)
  const [flags, setFlags] = useState<Flag[]>(() => {
    const f = params.get('flag')
    return FLAG_FILTERS.some((x) => x.key === f) ? [f as Flag] : []
  })
  const [attn, setAttn] = useState(false)
  // The filters behind the Filters button, and whether its panel is open.
  const [more, setMore] = useState<MoreFilters>(NO_MORE)
  const [moreOpen, setMoreOpen] = useState(false)
  const [view, setView] = useState<'table' | 'map'>('table')
  const [picked, setPicked] = useState<string[]>([])
  const [openId, setOpenId] = useState<string | null>(() => params.get('cycle'))
  // ?tab opens the linked cycle on that tab (the Strike Board's evidence link).
  const linkedTab = (['timeline', 'evidence', 'rules', 'audit'] as DrawerTab[]).find((t) => t === params.get('tab'))
  // A cycle opened from another cycle's audit trail opens on its own.
  const [openTab, setOpenTab] = useState<DrawerTab | undefined>()
  const [bulk, setBulk] = useState<BulkOp | null>(null)
  // The last bulk change, kept above the table with its one undo (h 2521)
  // until it is undone or dismissed; a toast would be gone before anyone
  // noticed the wrong cycles had moved.
  const [bulkDone, setBulkDone] = useState<{ id: string | null; message: string; error?: string } | null>(null)
  const [bulkUndoing, setBulkUndoing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [help, setHelp] = useHelpPref(user, 'product')

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)
  // The period the rows were loaded for: a refetch of it that fails keeps the rows up and says so; another period's failure shows none of the old.
  const dataKey = useRef<string | null>(null)
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    setError(null)
    try {
      const r = await apiJson<ListResponse>(u, `/api/product-activity?start_date=${from}&end_date=${to}`)
      if (n !== seq.current) return
      dataKey.current = `${from}_${to}`
      setData(r)
      setAsAt(new Date())
    } catch (e: any) {
      if (n !== seq.current) return
      if (dataKey.current !== `${from}_${to}`) {
        dataKey.current = null
        setData(null)
      }
      setError(e?.message || 'Could not load product activity')
    } finally {
      if (n === seq.current) setShown(`${from}_${to}`)
    }
  }, [uid, from, to])

  useEffect(() => {
    load()
  }, [load])
  // h 4167-4171: it fetches again on window focus and on reconnect, and a
  // period that includes today is pushed: a cycle or batch written on one of
  // its dates fetches it again.
  useRefetch(load)
  const loadLate = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    try {
      const r = await apiJson<LateCounts>(u, '/api/product-activity/late-data/count')
      setLateCount(r.total)
    } catch {
      // The badge is a hint; the tab itself says when it cannot load.
    }
  }, [uid])
  useEffect(() => {
    loadLate()
  }, [loadLate])
  useRefetch(() => {
    loadLate()
    setLateKey((k) => k + 1)
  })
  const includesToday = from <= today && to >= today
  const live = useLive(user, ['cycles', 'batches'], includesToday ? (d) => d >= from && d <= to : null, load)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const undoBulk = async () => {
    if (!bulkDone?.id || bulkUndoing) return
    setBulkUndoing(true)
    try {
      const r = await apiJson<{ message: string }>(userRef.current, `/api/product-activity/bulk/${bulkDone.id}/revert`, { body: {} })
      setBulkDone(null)
      setToast(r.message)
      load()
    } catch (e: any) {
      setBulkDone((b) => (b ? { ...b, error: e?.message || 'The undo was not saved' } : b))
    } finally {
      setBulkUndoing(false)
    }
  }

  const cycles = data?.cycles || []
  const states = data?.day_states || {}
  const dates = useMemo(() => datesBetween(from, to), [from, to])
  const tones = dates.map((d) => toneOf(d, today, states))
  const worst = strictest(tones)
  const mixed = new Set(tones).size > 1
  const lockedDate = (d: string) => toneOf(d, today, states) === 'locked'

  const late = tab === 'late'
  const excl = tab === 'excluded'
  const onTab = cycles.filter((c) => (c.status === 'excluded') === excl)

  // Filter options come from the cycles in the range, the way V4 builds them.
  const workerOpts = useMemo(() => {
    const names = [...new Set(cycles.map((c) => c.worker_name).filter(Boolean) as string[])].sort()
    return [ALL_WORKERS, ...names, ...(cycles.some((c) => !c.worker_name) ? [UNASSIGNED] : [])]
  }, [cycles])
  const jobOpts = useMemo(() => [ALL_JOBS, ...[...new Set(cycles.map((c) => c.picking_job_name).filter(Boolean) as string[])].sort()], [cycles])
  const productOpts = useMemo(() => [ALL_PRODUCTS, ...[...new Set(cycles.map((c) => c.product).filter(Boolean) as string[])].sort()], [cycles])
  // The Filters panel's lists: qualities and teams from the cycles in range,
  // locations from the tenant's structure grouped under their site.
  const qualityOpts = useMemo(() => [...new Set(cycles.map((c) => c.quality_name).filter(Boolean) as string[])].sort(), [cycles])
  const teamOpts = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of cycles) if (c.team_id) m.set(c.team_id, c.team_name || 'Team')
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [cycles])
  const siteOpts = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of data?.locations || []) m.set(l.site_id, l.site_name)
    for (const c of cycles) if (c.site_id && !m.has(c.site_id)) m.set(c.site_id, c.site_name || 'Site')
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [cycles, data])
  const locationName = (v: string) => {
    if (v.startsWith('site:')) return siteOpts.find(([id]) => 'site:' + id === v)?.[1] || 'Site'
    const l = (data?.locations || []).find((x) => x.id === v)
    return l ? `${l.site_name} · ${l.label}` : 'Location'
  }
  const teamName = (id: string) => teamOpts.find(([t]) => t === id)?.[1] || 'Team'
  const moreOn = moreCount(more)
  const moreText = moreSummary(more, { location: locationName, team: teamName })
  const setMoreF = (patch: Partial<MoreFilters>) => {
    setMore((m) => ({ ...m, ...patch }))
    setPicked([])
  }

  const q = query.trim().toLowerCase()
  const rows = onTab
    .filter((c) => {
      if (attn && !needsAttention(c)) return false
      if (!matchesMore(c, more)) return false
      for (const f of flags) if (!c.flags.includes(f)) return false
      if (worker !== ALL_WORKERS && (c.worker_name || UNASSIGNED) !== worker) return false
      if (job !== ALL_JOBS && c.picking_job_name !== job) return false
      if (product !== ALL_PRODUCTS && c.product !== product) return false
      if (q) {
        const hay = [c.item_code, itemName(c), c.tagless ? 'tagless' : '', c.worker_name || 'unassigned', c.batch_code, c.location, c.picking_job_name].join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
    .sort((a, b) => (a.device_captured_at < b.device_captured_at ? 1 : a.device_captured_at > b.device_captured_at ? -1 : 0))

  // Counted against the range and tab, not the filter, so the number does not
  // change out from under the button that filters by it (V4).
  const attention = onTab.filter(needsAttention).length
  const gross = rows.reduce((a, c) => a + (c.gross_kg || 0), 0)
  const credited = rows.reduce((a, c) => a + (c.credited_kg ?? c.gross_kg ?? 0), 0)
  const unweighed = unweighedSummary(rows)
  const clearOn = flags.length > 0 || worker !== ALL_WORKERS || job !== ALL_JOBS || product !== ALL_PRODUCTS || !!query || moreOn > 0
  const mapped = mapSplit(rows)

  const allOn = picked.length > 0 && picked.length === rows.length
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.concat(id)))

  const setRange = (f: string, t: string) => {
    setFrom(f)
    setTo(t)
    setPicked([])
    setOpenId(null)
  }

  // §7k.5: the Batch cell and the drawer's Batch line open that batch.
  const openBatch = (c: Cycle) =>
    navigate(`/batches?from=${c.work_date}&to=${c.work_date}&q=${encodeURIComponent(c.batch_code || '')}&batch=${encodeURIComponent(c.batch_id || '')}`)

  const exportXlsx = () => {
    const shown = rows
    const note = shown.length + ' cycle' + (shown.length === 1 ? '' : 's') + ' as shown · ' + rangeLabel(from, to)
    startTask(fileTask('Export product activity', note, 'product-activity.xlsx', XLSX_TYPE, () => workbook([{ name: 'Product activity', rows: exportRows(shown), widths: [11, 7, 10, 20, 16, 22, 18, 14, 12, 10, 10, 11, 14, 11, 30] }])))
  }

  const bannerTitle = mixed
    ? `This range spans ${new Set(tones).size} day states`
    : rangeLabel(from, to) + ' is ' + STATE_TEXT[worst].name.toLowerCase()
  const bannerLine = mixed
    ? 'Each day keeps its own state, so an action is allowed or refused per day. The strictest day in view is ' +
      STATE_TEXT[worst].name.toLowerCase() +
      ': ' +
      STATE_TEXT[worst].line.charAt(0).toLowerCase() +
      STATE_TEXT[worst].line.slice(1)
    : STATE_TEXT[worst].line

  const workers = data?.workers || []
  // An open filter list is a layer (h 425-458): Escape closes it.
  useLayer(!!drop, () => setDrop(null))
  useLayer(moreOpen, () => setMoreOpen(false))

  // Included / Excluded, over the table and the map alike.
  const tabTrack = (
    <div className={styles.tabTrack}>
      {(
        [
          ['included', 'Included', cycles.filter((c) => c.status !== 'excluded').length],
          ['excluded', 'Excluded', cycles.filter((c) => c.status === 'excluded').length],
          ['late', 'Late data', lateCount],
        ] as const
      ).map(([k, label, count]) => (
        <button
          key={k}
          type="button"
          className={tab === k ? styles.tabOn : styles.tab}
          onClick={() => {
            setTab(k)
            setPicked([])
            setAttn(false)
          }}
        >
          {label}
          {count !== null && (k === 'late' || !loading) && <span className={styles.tabCount} data-testid={k === 'late' ? 'late-count' : undefined}>{count}</span>}
        </button>
      ))}
    </div>
  )

  const drops: { key: 'worker' | 'job' | 'product'; label: string; value: string; options: string[]; set: (v: string) => void }[] = [
    { key: 'worker', label: 'Worker', value: worker, options: workerOpts, set: setWorker },
    { key: 'job', label: 'Picking job', value: job, options: jobOpts, set: setJob },
    { key: 'product', label: 'Product', value: product, options: productOpts, set: setProduct },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <StillLooking at={kept.at} onDefaults={from === today && to === today} label={datesLabel(from, to, today)} onContinue={kept.confirm} onDefault={() => { setFrom(today); setTo(today) }} />
      <PageHeader
        title="Product activity"
        tabs={<PairTabs label="Product activity and batches" tabs={productTabs(from, to)} current="Product activity" />}
        actions={
          <>
            <button type="button" className={headerStyles.btn} onClick={() => setImporting(true)}>
              Import weights
            </button>
            <button type="button" className={headerStyles.btnPrimary} onClick={exportXlsx} disabled={loading || !rows.length}>
              <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export current results
            </button>
            <button
              type="button"
              className={headerStyles.btnHelp}
              onClick={() => {
                setHelp(!help)
              }}
            >
              {help ? 'Hide help' : 'How product activity works'}
            </button>
          </>
        }
      />

      <PageBody className={styles.body}>
        {help && (
          <div className={styles.helpGrid}>
            <button
              type="button"
              className={styles.helpClose}
              onClick={() => setHelp(false)}
              aria-label="Close help"
              title="Close help"
            >
              ×
            </button>
            {HELP.map(([h, body]) => (
              <div key={h} className={styles.helpCard}>
                <span>{h}</span>
                <p>{body}</p>
              </div>
            ))}
          </div>
        )}

        <div className={styles.toolbar}>
          <DatePeriod user={user} from={from} to={to} onApply={setRange} />
          {!loading && <AsAt at={asAt} live={live === 'live'} className={styles.asAt} />}
        </div>

        <div className={styles.banner} style={{ background: TONE[worst].bg, borderColor: TONE[worst].line }} data-testid="day-banner">
          <span className={styles.bannerDot} style={{ background: TONE[worst].dot }} />
          <div className={styles.bannerText}>
            <div className={styles.bannerTitle} style={{ color: TONE[worst].ink }}>
              {bannerTitle}
            </div>
            <div className={styles.bannerLine} style={{ color: TONE[worst].ink }}>
              {bannerLine}
            </div>
            {dates.length <= 6 && dates.length > 1 && (
              <div className={styles.bannerChips}>
                {dates.map((d, i) => (
                  <span key={d} className={styles.bannerChip} style={{ background: TONE[tones[i]].bg, borderColor: TONE[tones[i]].line, color: TONE[tones[i]].ink }}>
                    <i style={{ background: TONE[tones[i]].dot }} />
                    {dayShort(d)} · {STATE_TEXT[tones[i]].name}
                  </span>
                ))}
              </div>
            )}
            {worst === 'live' && (
              <div className={styles.bannerChips}>
                <span className={styles.bannerChip} style={{ background: TONE.live.bg, borderColor: TONE.live.line, color: TONE.live.ink }}>
                  <i style={{ background: TONE.live.dot }} />
                  {dayShort(to)} · Live
                </span>
              </div>
            )}
          </div>
          {mixed && (
            <button type="button" className={styles.narrowBtn} onClick={() => setRange(to, to)}>
              Show {dayLabel(to)} only
            </button>
          )}
        </div>

        <div className={styles.filters}>
          <div className={styles.searchWrap}>
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.searchIcon}>
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input className={styles.search} placeholder="Item code, worker, batch…" aria-label="Search cycles" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          {drops.map((d) => (
            <div key={d.key} className={styles.dropWrap}>
              <button type="button" className={styles.dropBtn} onClick={() => {
                  setMoreOpen(false)
                  setDrop(drop === d.key ? null : d.key)
                }} aria-haspopup="listbox" aria-expanded={drop === d.key}>
                <span className={styles.dropLabel}>{d.label}</span>
                <span className={styles.dropValue}>{d.value}</span>
                <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.dropChevron}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {drop === d.key && (
                <div className={styles.dropMenu} role="listbox" aria-label={d.label}>
                  {d.options.map((o) => (
                    <button
                      key={o}
                      type="button"
                      role="option"
                      aria-selected={d.value === o}
                      className={d.value === o ? styles.dropOptOn : styles.dropOpt}
                      onClick={() => {
                        d.set(o)
                        setDrop(null)
                        setPicked([])
                      }}
                    >
                      <span>{o}</span>
                      <span className={styles.tick}>{d.value === o ? '✓' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className={styles.dropWrap}>
            <button
              type="button"
              className={moreOpen || moreOn ? styles.moreBtnOn : styles.moreBtn}
              onClick={() => {
                setDrop(null)
                setMoreOpen(!moreOpen)
              }}
              aria-expanded={moreOpen}
            >
              Filters
              {moreOn > 0 && (
                <span className={styles.moreCount} aria-label={`${moreOn} in use`}>
                  {moreOn}
                </span>
              )}
            </button>
            {moreOpen && (
              <div className={styles.morePop} role="dialog" aria-label="Filters">
                <div className={styles.moreSection}>
                  <span className={styles.moreLabel}>Time of day</span>
                  <div className={styles.moreRow}>
                    <select className={styles.moreSelect} aria-label="Time by" value={more.timeBy} onChange={(e) => setMoreF({ timeBy: e.target.value as TimeBasis })}>
                      {TIME_BASES.map((b) => (
                        <option key={b.key} value={b.key}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.moreRow}>
                    <input className={styles.moreInput} type="time" aria-label="From time" value={more.timeFrom} onChange={(e) => setMoreF({ timeFrom: e.target.value })} />
                    <span className={styles.moreTo}>to</span>
                    <input className={styles.moreInput} type="time" aria-label="To time" value={more.timeTo} onChange={(e) => setMoreF({ timeTo: e.target.value })} />
                  </div>
                  <span className={styles.moreHint}>On the site's clock, every day in the period. Both ends count; 22:00 to 02:00 runs over midnight.</span>
                </div>
                <div className={styles.moreSection}>
                  <span className={styles.moreLabel}>Gross weight (kg)</span>
                  <div className={styles.moreRow}>
                    <input className={styles.moreInput} type="number" min="0" step="0.1" inputMode="decimal" placeholder="Min" aria-label="Minimum gross kg" value={more.minKg} onChange={(e) => setMoreF({ minKg: e.target.value })} />
                    <span className={styles.moreTo}>to</span>
                    <input className={styles.moreInput} type="number" min="0" step="0.1" inputMode="decimal" placeholder="Max" aria-label="Maximum gross kg" value={more.maxKg} onChange={(e) => setMoreF({ maxKg: e.target.value })} />
                  </div>
                  <span className={styles.moreHint}>The effective gross. A cycle not weighed yet is outside any range.</span>
                </div>
                <div className={styles.moreSection}>
                  <span className={styles.moreLabel}>Quality</span>
                  <select className={styles.moreSelect} aria-label="Quality" value={more.quality} onChange={(e) => setMoreF({ quality: e.target.value })}>
                    <option value="">Any quality</option>
                    {qualityOpts.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                    <option value={NO_QUALITY}>No quality</option>
                  </select>
                </div>
                <div className={styles.moreSection}>
                  <span className={styles.moreLabel}>Location</span>
                  <select className={styles.moreSelect} aria-label="Location" value={more.location} onChange={(e) => setMoreF({ location: e.target.value })}>
                    <option value="">Anywhere</option>
                    {siteOpts.map(([sid, sname]) => (
                      <optgroup key={sid} label={sname}>
                        <option value={'site:' + sid}>All of {sname}</option>
                        {(data?.locations || [])
                          .filter((l) => l.site_id === sid)
                          .map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.label}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className={styles.moreSection}>
                  <span className={styles.moreLabel}>Team</span>
                  <select className={styles.moreSelect} aria-label="Team" value={more.team} onChange={(e) => setMoreF({ team: e.target.value })}>
                    <option value="">Any team</option>
                    {teamOpts.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                    <option value={NOT_CLOCKED}>Not clocked on</option>
                  </select>
                  <span className={styles.moreHint}>The team the worker was clocked on to when the cycle was captured.</span>
                </div>
                <div className={styles.moreFoot}>
                  <span className={styles.moreMuted}>{moreOn === 0 ? 'Nothing set here' : `${rows.length} cycle${rows.length === 1 ? '' : 's'} match`}</span>
                  {moreOn > 0 && (
                    <button type="button" className={styles.clearLink} onClick={() => setMoreF(NO_MORE)}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          {moreText.length > 0 && (
            <span className={styles.moreLine} data-testid="more-summary">
              Only {moreText.join(' · ')}
            </span>
          )}
        </div>

        <div className={styles.flagRow}>
          {FLAG_FILTERS.map((f) => {
            const on = flags.includes(f.key)
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={on}
                className={on ? styles.flagOn : styles.flag}
                onClick={() => setFlags(on ? flags.filter((x) => x !== f.key) : flags.concat(f.key))}
              >
                {f.label}
              </button>
            )
          })}
          {clearOn && (
            <button
              type="button"
              className={styles.clearLink}
              onClick={() => {
                setFlags([])
                setWorker(ALL_WORKERS)
                setJob(ALL_JOBS)
                setProduct(ALL_PRODUCTS)
                setQuery('')
                setMore(NO_MORE)
              }}
            >
              Clear filters
            </button>
          )}
          <div className={styles.summary}>
            {/* Counts and totals wait for the period's rows, not the last one's. */}
            {!loading && (
              <>
                <strong data-testid="summary">
                  {rows.length} cycle{rows.length === 1 ? '' : 's'}
                </strong>
                <span className={styles.totals}>
                  {gross.toFixed(1)} kg gross · {credited.toFixed(1)} kg credited
                  {unweighed && (
                    <span className={styles.numEst} title={EST_TITLE} data-testid="unweighed">
                      {' · '}
                      {unweighed}
                    </span>
                  )}
                </span>
              </>
            )}
            {!loading && (attention > 0 || attn) && (
              <button
                type="button"
                className={attn ? styles.attnOn : styles.attn}
                aria-pressed={attn}
                title={attn ? 'Show everything again' : 'Show only cycles needing attention'}
                onClick={() => setAttn(!attn)}
              >
                {attn ? `Showing ${attention} needing attention` : `${attention} need attention`}
                {attn && <span aria-hidden> ×</span>}
              </button>
            )}
            <div className={styles.viewTrack}>
              <button type="button" className={view === 'table' ? styles.viewOn : styles.view} onClick={() => setView('table')} title="Show the cycles as a table">
                Table
              </button>
              <button type="button" className={view === 'map' ? styles.viewOn : styles.view} onClick={() => setView('map')} title="Show the same cycles on the map">
                Map
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className={styles.errorBar} role="alert">
            <span>{data ? `Product activity could not be refreshed: ${error}. What is shown may be out of date.` : error}</span>
            <button type="button" className={styles.flashX} onClick={() => load()}>
              Retry
            </button>
          </div>
        )}
        {data?.truncated && <div className={styles.warnBar}>Only the newest 5,000 cycles in this range are shown. Narrow the period to see the rest.</div>}

        {view === 'map' && !late ? (
          <div className={styles.card}>
            <div className={styles.cardHead}>
              {tabTrack}
              <div className={styles.mapLegend} data-testid="map-legend">
                {Object.values(MAP_STATUS).map((s) => (
                  <span key={s.label} className={styles.legendItem}>
                    <i style={{ background: s.fill }} />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
            {loading ? (
              <div className={styles.empty}>Loading product activity…</div>
            ) : !rows.length ? (
              <div className={styles.empty}>{error && !data ? 'Product activity could not be loaded.' : 'No cycles match these filters.'}</div>
            ) : !mapped.placed.length ? (
              <div className={styles.empty} data-testid="map-empty">
                {rows.length === 1
                  ? 'The one cycle in view has no recorded GPS point, so there is nothing to place on the map. The table shows it.'
                  : `None of the ${rows.length} cycles in view has a recorded GPS point, so there is nothing to place on the map. The table shows them.`}
              </div>
            ) : (
              <>
                {mapped.unplaced > 0 && (
                  <div className={styles.mapNote} data-testid="map-unplaced">
                    {mapped.unplaced} of {rows.length} cycles in view {mapped.unplaced === 1 ? 'has' : 'have'} no recorded GPS point and {mapped.unplaced === 1 ? 'is' : 'are'} not on the map.
                  </div>
                )}
                <div className={styles.mapBody}>
                  <Suspense fallback={<div className={styles.empty}>Loading the map…</div>}>
                    <CycleMap
                      cycles={mapped.placed}
                      openId={openId}
                      onOpen={(id) => {
                        setOpenTab(undefined)
                        setOpenId(id)
                      }}
                    />
                  </Suspense>
                </div>
              </>
            )}
          </div>
        ) : late ? (
          <div className={styles.card}>
            <div className={styles.cardHead}>{tabTrack}</div>
            <LateDataPanel
              user={user}
              canEdit={can(access, 'product_activity', 'edit')}
              refreshKey={lateKey}
              toast={setToast}
              onOpenConflict={can(access, 'batches') ? (id) => navigate(`/batches?conflict=${encodeURIComponent(id)}`) : undefined}
              onOpenDay={hasPower(access, 'day_lock') ? (date) => navigate(`/timesheets?date=${encodeURIComponent(date)}`) : undefined}
              onChanged={() => {
                loadLate()
                load()
              }}
            />
          </div>
        ) : (
          <div className={styles.card}>
            <div className={styles.cardHead}>{tabTrack}</div>

            {picked.length > 0 && (
              <div className={styles.selBar}>
                <span className={styles.selLabel}>
                  {picked.length} cycle{picked.length === 1 ? '' : 's'} selected
                </span>
                <div className={styles.selBtns}>
                  {BULK_BUTTONS.map((b) => (
                    <button
                      key={b.op}
                      type="button"
                      className={styles.selBtn}
                      onClick={() => setBulk(b.op)}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                <button type="button" className={styles.selClear} onClick={() => setPicked([])}>
                  Clear selection
                </button>
              </div>
            )}

            {bulkDone && (
              <div className={styles.bulkDone} role="status" data-testid="bulk-done">
                <span>
                  {bulkDone.message}
                  {bulkDone.error && <span className={styles.bulkDoneErr}> · {bulkDone.error}</span>}
                </span>
                {bulkDone.id && (
                  <PendingButton busy={bulkUndoing} pending="Undoing…" className={styles.ghostSmall} onClick={undoBulk}>
                    Undo bulk edit
                  </PendingButton>
                )}
                <button type="button" className={styles.selClear} onClick={() => setBulkDone(null)} disabled={bulkUndoing}>
                  Dismiss
                </button>
              </div>
            )}

            {!loading && rows.length === 0 ? (
              <div className={styles.empty}>{error && !data ? 'Product activity could not be loaded.' : 'No cycles match these filters.'}</div>
            ) : (
              <div className={styles.table} role="table" aria-label="Product cycles">
                <div className={styles.headRow} role="row">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={allOn}
                    aria-label="Select every cycle shown"
                    className={allOn ? styles.checkOn : styles.check}
                    disabled={loading}
                    onClick={() => setPicked(allOn ? [] : rows.map((r) => r.id))}
                  >
                    {allOn ? '✓' : ''}
                  </button>
                  {['Date & time', 'Item', 'Worker', 'Location', 'Picking job', 'Product', 'Batch', 'Weight', 'Credited', 'Notes'].map((h) => (
                    <span key={h} role="columnheader">
                      {h}
                    </span>
                  ))}
                </div>
                {loading ? (
                  <SkeletonRows rowClass={styles.row} cols={11} label="Loading product activity" />
                ) : (
                  rows.map((c) => (
                    <CycleRow
                      key={c.id}
                      c={c}
                      tone={toneOf(c.work_date, today, states)}
                      on={picked.includes(c.id)}
                      open={openId === c.id}
                      onPick={() => toggle(c.id)}
                      onOpen={() => {
                        setOpenTab(undefined)
                        setOpenId(c.id)
                      }}
                      onBatch={() => openBatch(c)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {openId && (
          <CycleDrawer
            key={openId}
            user={user}
            cycleId={openId}
            workers={workers}
            onClose={() => {
              setOpenTab(undefined)
              setOpenId(null)
            }}
            onWritten={load}
            toast={setToast}
            onBatch={openBatch}
            initialTab={openTab || (openId === params.get('cycle') ? linkedTab : undefined)}
            onOpenCycle={(id) => {
              setOpenTab('audit')
              setOpenId(id)
            }}
          />
        )}
        {bulk && (
          <BulkModal
            user={user}
            op={bulk}
            cycles={cycles.filter((c) => picked.includes(c.id))}
            lockedDate={lockedDate}
            workers={workers}
            locations={data?.locations || []}
            onClose={() => setBulk(null)}
            onApplied={(msg, id) => {
              setBulk(null)
              setPicked([])
              setToast('')
              setBulkDone({ id, message: msg })
              load()
            }}
            onError={setToast}
          />
        )}
        {importing && (
          <WeightImportDialog
            user={user}
            onClose={() => setImporting(false)}
            onDone={(msg) => {
              setImporting(false)
              // Kept above the table like a bulk edit's result; an import has
              // no undo of its own (h 2522), each line is undone on its cycle.
              setBulkDone({ id: null, message: msg })
              load()
            }}
          />
        )}
        {toast && (
          <div className={styles.toast} role="status">
            {toast}
          </div>
        )}
      </PageBody>
    </div>
  )
}

function CycleRow({ c, tone, on, open, onPick, onOpen, onBatch }: { c: Cycle; tone: DayTone; on: boolean; open: boolean; onPick: () => void; onOpen: () => void; onBatch: () => void }) {
  const chips = CHIP_ORDER.filter((f) => f !== 'excluded' && c.flags.includes(f))
  if (c.display_status === 'inferred' && !chips.includes('inferred')) chips.push('inferred')
  return (
    <div
      className={[styles.row, open ? styles.rowOpen : '', c.status === 'excluded' ? styles.rowExcluded : ''].join(' ')}
      role="row"
      data-testid="cycle-row"
      {...openableRow(onOpen)}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        aria-label={'Select ' + itemName(c)}
        className={on ? styles.checkOn : styles.check}
        onClick={(e) => {
          e.stopPropagation()
          onPick()
        }}
      >
        {on ? '✓' : ''}
      </button>
      <div className={styles.when}>
        <span className={styles.whenTime}>{c.eff_time || '—'}</span>
        <span className={styles.whenDay} style={{ color: TONE[tone].ink }}>
          {dayShort(c.work_date)}
        </span>
      </div>
      <span className={[styles.item, styles.itemAccent].join(' ')}><ItemName c={c} /></span>
      <span className={c.worker_name ? styles.cell : styles.cellBad}>{c.worker_name || 'Unassigned'}</span>
      <span className={styles.cellMuted}>{c.location || '—'}</span>
      <span className={styles.cellMuted}>{c.picking_job_name || '—'}</span>
      <span className={styles.cellMuted}>{c.product || '—'}</span>
      {c.batch_code ? (
        <button
          type="button"
          className={styles.batch}
          title={'Open batch ' + c.batch_code + ' in Batches'}
          onClick={(e) => {
            e.stopPropagation()
            onBatch()
          }}
        >
          {c.batch_code}
        </button>
      ) : (
        <span className={styles.cellFaint}>—</span>
      )}
      {c.gross_kg === null && c.estimated_kg ? (
        <span className={styles.numEst} title={EST_TITLE}>
          {estKg(c.estimated_kg)}
        </span>
      ) : (
        <span className={styles.num} title={c.weight_estimated ? WEIGHT_EST_TITLE : undefined}>
          {kg(c.gross_kg)}
        </span>
      )}
      {c.qc_factor !== null ? <span className={styles.numCredited}>{kg(c.credited_kg)}</span> : <span className={styles.cellFaint}>—</span>}
      <div className={styles.chips}>
        {chips.map((f) => {
          const ch = CHIP[f]!
          return (
            <span key={f} className={styles.chip} style={{ background: ch.bg, color: ch.fg }}>
              {ch.label}
            </span>
          )
        })}
        {/* A matched QC waiting for the day close: Credited stays at gross until then. */}
        {c.qc_factor === null && c.qc_provisional && (
          <span className={styles.chip} style={{ background: CHIP.qc!.bg, color: CHIP.qc!.fg }} title="Applied when the day closes" data-testid="qc-provisional-chip">
            QC {c.qc_provisional.qc_factor === null ? '' : pct(c.qc_provisional.qc_factor) + ' '}· provisional
          </span>
        )}
      </div>
    </div>
  )
}
